import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { viridisColor, viridisTextColor, normalizeValue } from '@/lib/viridis';
import { getDisplayRows } from '@/lib/heatmapViewPreset';
import type { ExposureRow, ExposureType } from '@/store/heatmapStore';

export interface TrinityPanelTableHandle {
  scrollToSpot: () => void;
  scrollToKing: () => void;
}

interface TrinityPanelTableProps {
  rows: ExposureRow[];
  spotPrice: number;
  type: ExposureType;
  visibleStrikeCount?: number | null;
}

const STRIKE_COLUMN_WIDTH = 58;
const ROW_HEIGHT = 20;
const CELL_HORIZONTAL_PADDING = 6;

function findSpotRowIndex(rows: ExposureRow[], spotPrice: number): number {
  let closest = 0;
  let minDist = Infinity;
  for (let i = 0; i < rows.length; i++) {
    const dist = Math.abs(rows[i].strike - spotPrice);
    if (dist < minDist) {
      minDist = dist;
      closest = i;
    }
  }
  return closest;
}

// Center `target` within its own scroll container by mutating only that
// container's scrollTop. Unlike scrollIntoView, this never scrolls ancestor
// containers — critical on mobile where all three panels share one vertical
// scroller and scrollIntoView would drag it to the last panel that centered.
function centerRowInScroller(scroller: HTMLElement | null, target: HTMLElement | null): void {
  if (scroller == null || target == null || typeof scroller.scrollTo !== 'function') {
    return;
  }

  const scrollerRect = scroller.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const delta =
    targetRect.top - scrollerRect.top - (scroller.clientHeight - target.clientHeight) / 2;
  scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: 'smooth' });
}

export const TrinityPanelTable = forwardRef<TrinityPanelTableHandle, TrinityPanelTableProps>(
  function TrinityPanelTable({ rows, spotPrice, type, visibleStrikeCount }, ref) {
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const spotRowRef = useRef<HTMLTableRowElement | null>(null);
    const kingCellRef = useRef<HTMLTableCellElement | null>(null);
    const displayRows = useMemo(
      () => getDisplayRows(rows, spotPrice, visibleStrikeCount),
      [rows, spotPrice, visibleStrikeCount],
    );

    const { colorMin, colorMax, spotRowIndex, kingRowIndex } = useMemo(() => {
      let min = Infinity;
      let max = -Infinity;
      let kingMagnitude = Number.NEGATIVE_INFINITY;
      let kingRowIndex = 0;

      for (let rowIndex = 0; rowIndex < displayRows.length; rowIndex++) {
        const total = displayRows[rowIndex].total;
        if (total == null) {
          continue;
        }

        if (total.raw < min) min = total.raw;
        if (total.raw > max) max = total.raw;
        const magnitude = Math.abs(total.raw);
        if (magnitude > kingMagnitude) {
          kingMagnitude = magnitude;
          kingRowIndex = rowIndex;
        }
      }

      return {
        colorMin: Number.isFinite(min) ? min : 0,
        colorMax: Number.isFinite(max) ? max : 0,
        kingRowIndex,
        spotRowIndex: findSpotRowIndex(displayRows, spotPrice),
      };
    }, [displayRows, spotPrice]);

    useImperativeHandle(ref, () => ({
      scrollToSpot: () => {
        centerRowInScroller(scrollerRef.current, spotRowRef.current);
      },
      scrollToKing: () => {
        centerRowInScroller(scrollerRef.current, kingCellRef.current);
      },
    }));

    if (displayRows.length === 0) {
      return (
        <div className="flex h-full items-center justify-center font-mono text-[11px] text-zinc-500">
          No data available
        </div>
      );
    }

    const minRow = displayRows.reduce((lowest, row) =>
      row.total.raw < lowest.total.raw ? row : lowest,
    );
    const maxRow = displayRows.reduce((highest, row) =>
      row.total.raw > highest.total.raw ? row : highest,
    );

    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollerRef}
          className="min-h-0 flex-1 overflow-y-auto [overscroll-behavior:contain]"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <table
            className="w-full border-collapse font-mono"
            style={{ tableLayout: 'fixed', WebkitUserSelect: 'none', userSelect: 'none' }}
          >
            <colgroup>
              <col style={{ width: STRIKE_COLUMN_WIDTH }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th
                  className="sticky top-0 z-10 bg-[#0d0d0d] px-1.5 py-1 text-left text-[9px] font-semibold text-white/85"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
                >
                  Strike
                </th>
                <th
                  className="sticky top-0 z-10 bg-[#0d0d0d] px-1.5 py-1 text-right text-[9px] font-semibold text-white/85"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
                >
                  {type.toUpperCase()} Total
                </th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, rowIndex) => {
                const isSpotRow = rowIndex === spotRowIndex;
                const isKingRow = rowIndex === kingRowIndex;
                const t = normalizeValue(row.total.raw, colorMin, colorMax);

                return (
                  <tr key={row.strike} ref={isSpotRow ? spotRowRef : undefined}>
                    <td
                      style={{
                        height: ROW_HEIGHT,
                        padding: `1px ${CELL_HORIZONTAL_PADDING}px`,
                        fontSize: 11,
                        fontWeight: isSpotRow ? 700 : 500,
                        color: isSpotRow ? '#000' : 'rgba(255,255,255,0.8)',
                        backgroundColor: isSpotRow ? '#fff' : '#0d0d0d',
                        whiteSpace: 'nowrap',
                        ...(isSpotRow
                          ? {
                              borderRadius: '4px 0 0 4px',
                              clipPath:
                                'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)',
                            }
                          : {}),
                      }}
                    >
                      {row.strike.toFixed(1)}
                    </td>
                    <td
                      ref={isKingRow ? kingCellRef : undefined}
                      style={{
                        height: ROW_HEIGHT,
                        padding: `1px ${CELL_HORIZONTAL_PADDING}px`,
                        fontSize: 10,
                        fontWeight: isKingRow ? 700 : 400,
                        color: viridisTextColor(t),
                        backgroundColor: viridisColor(t),
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        boxShadow: isKingRow ? 'inset 0 0 0 2px rgba(168,85,247,0.6)' : undefined,
                      }}
                    >
                      {row.total.formatted}
                      {isKingRow ? <span className="ml-0.5 inline-block text-[7px]">★</span> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex shrink-0 items-center gap-2.5 border-t border-white/[.06] px-2.5 py-2">
          <span className="whitespace-nowrap font-mono text-[10px] text-zinc-400">
            {minRow.total.formatted}
          </span>
          <div
            className="h-1.5 flex-1 rounded-full"
            style={{
              background:
                'linear-gradient(to right, #440154, #471d6e, #403a83, #3c4a86, #345f8d, #287a8e, #1f968b, #35af7a, #95cb45, #eddd27, #fde725)',
            }}
          />
          <span className="whitespace-nowrap font-mono text-[10px] text-zinc-400">
            {maxRow.total.formatted}
          </span>
        </div>
      </div>
    );
  },
);
